import { Link } from 'react-router-dom';
import type { ComicItem } from '@/mocks/comics';

interface ComicCardProps {
  comic: ComicItem;
  variant?: 'hot' | 'editor' | 'default';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  const statusLabel = comic.status === 'ongoing' ? '连载中' : '已完结';
  const statusColor = comic.status === 'ongoing' ? 'bg-primary-500' : 'bg-secondary-500';

  if (variant === 'editor') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group block cursor-pointer"
        data-product-shop="true"
      >
        <div className="rounded-3xl border-[1.5px] border-primary-200/60 overflow-hidden bg-white transition-all duration-300 hover:border-primary-300/80">
          <div className="relative aspect-[3/4] overflow-hidden">
            <img
              src={comic.coverImage}
              alt={comic.title}
              title={`${comic.title} - 桃桃漫画网韩国漫画推荐`}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white/90 to-transparent" />
          </div>
          <div className="px-5 pb-5 pt-3">
            <h3 className="font-heading text-[17px] font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
              {comic.title}
            </h3>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs text-foreground-400">{comic.author}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300" />
              <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor} text-white font-medium`}>
                {statusLabel}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'hot') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group flex-shrink-0 w-[200px] md:w-[220px] cursor-pointer"
        data-product-shop="true"
      >
        <div className="rounded-2xl bg-background-100 overflow-hidden transition-all duration-300 hover:translate-y-[-4px]">
          <div className="relative aspect-[3/4] overflow-hidden">
            <img
              src={comic.coverImage}
              alt={comic.title}
              title={`${comic.title} - 热门韩国漫画`}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute top-3 left-3">
              <span className={`text-[11px] px-2.5 py-1 rounded-full ${statusColor} text-white font-medium`}>
                {statusLabel}
              </span>
            </div>
          </div>
          <div className="p-3.5">
            <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1">{comic.title}</h3>
            <div className="flex items-center justify-between mt-2">
              <span className="text-[12px] text-foreground-400">{comic.category}</span>
              <span className="text-[12px] font-bold text-foreground-700 whitespace-nowrap">
                更新至 {comic.chapters} 话
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group block cursor-pointer"
      data-product-shop="true"
    >
      <div className="rounded-2xl bg-white overflow-hidden transition-all duration-300 hover:translate-y-[-3px] border border-background-200/60">
        <div className="relative aspect-[3/4] overflow-hidden">
          <img
            src={comic.coverImage}
            alt={comic.title}
            title={`${comic.title} - 桃桃漫画网`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute top-3 left-3">
            <span className={`text-[11px] px-2.5 py-1 rounded-full ${statusColor} text-white font-medium`}>
              {statusLabel}
            </span>
          </div>
        </div>
        <div className="p-4">
          <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
            {comic.title}
          </h3>
          <div className="flex items-center gap-1.5 mt-2">
            <i className="ri-star-fill text-amber-400 text-xs" style={{ width: '12px', height: '12px' }} />
            <span className="text-xs text-foreground-500">{comic.rating}</span>
            <span className="text-xs text-foreground-300 ml-1">{comic.chapters}话</span>
          </div>
        </div>
      </div>
    </Link>
  );
}